// @flow
import { decodeHTML } from 'entities';
import { get, includes, isEmpty, isUndefined } from 'lodash';
import { resolve, reject } from 'redux-optimist-promise';

import { getTimestamp } from 'Utils/date/dateUtils';
import {
  PRODUCT_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  LOCALE_FACET_ID,
  MAX_NUMBER_OF_SELECTED_PRODUCTS,
  DEPARTURE_CITY_FACET_ID,
  CHILDCARE_FACET_ID,
  ACTIVITIES_FACET_ID,
  COUNTRIES_FACET_ID,
  DESTINATIONS_FACET_ID,
  FILTERABLE_FACETS_IDS,
  TYPES_OF_STAY_FACET_ID,
  FACET_COMFORT_ID,
  DURATION_FACET,
  QUERY_QUERY_PARAM,
} from 'Utils/faceting/constants';
import { SYNC_WITH_LOCATION } from 'Store/constants';
import createReducer from 'Store/utils/createReducer';
import { type ValidationState } from 'Store/trip-information/kids/constants';
import {
  FACETING_CLEAR_FACETS,
  FACETING_SEARCH_REQUEST,
  FACETING_SET_SEARCH_QUERY,
  FACETING_SET_FILTERS,
  FACETING_SET_KIDS_VALIDATION_STATE,
  FACETING_SET_PRODUCT_COMPARISON_SELECTION_MODE,
  FACETING_TOGGLE_PRODUCT_SELECTED_FOR_COMPARISON,
  FACETING_TOGGLE_FACET_SELECTED,
  FACETING_SET_INITIAL_PRICES,
  FACETING_SET_IS_DATE_REQUIRED,
  FACETING_SET_IS_SEARCH_ENABLED,
  FACETING_RESET_REDIRECTION,
  FACETING_RESET_SEARCH,
  FACETING_PRICES_REQUEST,
  FACETING_CHILDCARE_REQUEST,
  FACETING_UPDATE_FILTERS,
  FACETING_LOAD_PRODUCTS_REQUEST,
} from './constants';

const IGNORED_FILTERS_IDS = [
  PRODUCT_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  LOCALE_FACET_ID,
  COUNTRIES_FACET_ID,
];

function catalogFacetItemToState(item: PertimmFacet): SearchFacetList {
  return {
    label: item.i18n,
    value: item.filter,
    count: item.freq,
  };
}

// Pertimm will always send `interpretation_explanation.interpretations` dates
// in the format 'DD/MM/YYYY', for any locale
const PERTIMM_INTERPRETATION_DATE_FORMAT = 'DD/MM/YYYY';

export const PERTIMM_DEPARTURE_MIN_DATE = 'departure_date_min';
export const PERTIMM_DEPARTURE_MAX_DATE = 'departure_date_max';

/**
 * Takes the Pertimm catalog of facets and restructure it before storage in redux store.
 */
export function catalogToState(
  catalogs: Array<PertimmFacetCategory> = [],
): Array<SearchFacetCategory> {
  const catalogIdsAsList = [
    DEPARTURE_CITY_FACET_ID,
    COUNTRIES_FACET_ID,
    CHILDCARE_FACET_ID,
    TYPES_OF_STAY_FACET_ID,
    FACET_COMFORT_ID,
    DURATION_FACET,
  ];
  const catalogIdsAsTree = [ACTIVITIES_FACET_ID, DESTINATIONS_FACET_ID];

  const facetsAsList = catalogs
    .filter(catalog => catalogIdsAsList.includes(catalog.id))
    .map(({ id, name, list }) => ({
      id,
      name,
      // $FlowFixMe
      list: list.map(catalogFacetItemToState),
    }));

  const facetsAsTree = catalogs
    .filter(catalog => catalogIdsAsTree.includes(catalog.id))
    .map(({ id, name, tree }) => ({
      id,
      name,
      // $FlowFixMe
      list: tree.map(list => ({
        id: list.name,
        ...catalogFacetItemToState(list),
        list: list.tree.map(catalogFacetItemToState),
      })),
    }));

  return facetsAsList.concat(facetsAsTree);
}

export function filtersToActiveFacetsState(filters: Array<PertimmActiveFacet> = []) {
  return filters.reduce((acc, filter) => {
    const categoryId = filter.id;
    if (FILTERABLE_FACETS_IDS.includes(categoryId)) {
      return { ...acc, [categoryId]: { ...acc[categoryId], [filter.value]: true } };
    }
    return acc;
  }, {});
}

function sanitizeFilters(filters: Array<PertimmActiveFacet>): Array<*> {
  return filters.filter(filter => !includes(IGNORED_FILTERS_IDS, get(filter, 'id')));
}

export function filtersToState(filters: Array<PertimmActiveFacet> = []): Array<*> {
  return sanitizeFilters(filters).map(filter => ({
    id: get(filter, 'id'),
    name: get(filter, 'value'),
    label: get(filter, 'value_i18n'),
    selected: true,
  }));
}

function parseBanners(banners = []) {
  try {
    return banners.map(banner => ({
      ...banner,
      content: JSON.parse(banner.content),
    }));
  } catch (e) {
    return [];
  }
}

export function removeUnselectedFiltersFromActiveFacetsState(
  filters: Array<PertimmActiveFacet> = [],
  activeFacetsState: {
    [string]: {
      [string]: {
        [string]: boolean,
      },
    },
  },
) {
  const activeFacets = { ...activeFacetsState };
  const newActiveFacets = {};
  if (filters.length) {
    for (const categoryId in activeFacets) {
      if (activeFacets.hasOwnProperty(categoryId)) {
        const categoryIdFilter = [...filters].filter(({ id }) => id === categoryId);
        if (categoryIdFilter.length) {
          newActiveFacets[categoryId] = {};
          for (const categoryName in activeFacets[categoryId]) {
            if (
              activeFacets[categoryId].hasOwnProperty(categoryName) &&
              // $FlowFixMe
              categoryIdFilter.some(({ name, id }) => name === categoryName && id === categoryId)
            ) {
              newActiveFacets[categoryId][categoryName] = activeFacets[categoryId][categoryName];
            }
          }
        }
      }
    }
  }
  return newActiveFacets;
}

export const initialState = {
  // Special pertimm marchandising rules with pre-parameterized criteria
  // such as `minDate` / `maxDate` for some user queries such as `noel`.
  pertimmInterpretations: {},
  products: [],
  recommandedProducts: [],
  facets: [],
  activeFacets: {},
  childcareAvailabilities: [],
  filters: [],
  query: { label: '' },
  redirection: null,
  scrollToResults: false,
  isLoading: true,
  isPriceLoading: true,
  isLoadingChildcareAvailabilities: false,
  isSearchEnabled: false,
  isSelectionModeEnabled: false,
  kidsValidationState: [],
  productsSelectedForComparison: [],
  prices: [],
  noResults: false,
  lastRequestId: -1,
  lastRecommandedRequestId: -1,
  isDateRequired: false,
};

export const facetingReducer = createReducer(initialState, {
  [FACETING_RESET_REDIRECTION](state: ReduxState) {
    return {
      ...state,
      redirection: null,
    };
  },

  [FACETING_RESET_SEARCH](state: ReduxState) {
    return {
      ...state,
      products: [],
      prices: [],
      childcareAvailabilities: [],
      redirection: null,
      scrollToResults: false,
      isLoading: true,
      noResults: false,
      searchIsEnabled: false,
    };
  },

  [FACETING_LOAD_PRODUCTS_REQUEST](state: ReduxState, action: LoadProductsRequestResolvedAction) {
    return {
      ...state,
      childcareAvailabilities: [],
      isLoading: true,
      lastRecommandedRequestId: get(action, 'optimist.id', -1),
      recommandedProducts: [],
      noResults: true,
      prices: [],
    };
  },

  [resolve(FACETING_LOAD_PRODUCTS_REQUEST)](
    state: ReduxState,
    action: LoadProductsRequestResolvedAction,
  ): ReduxState {
    // If there is another request in progress
    if (action?.optimist?.id !== state.lastRecommandedRequestId) {
      return state;
    }
    const { resorts } = action.payload;

    return {
      ...state,
      isLoading: false,
      recommandedProducts: resorts,
      noResults: true,
    };
  },

  [reject(FACETING_LOAD_PRODUCTS_REQUEST)](state: ReduxState): ReduxState {
    return {
      ...state,
      isLoading: false,
      noResults: true,
    };
  },

  [FACETING_SEARCH_REQUEST](state: ReduxState, action: FacetingSearchRequestResolvedAction) {
    return {
      ...state,
      childcareAvailabilities: [],
      isLoading: true,
      lastRequestId: get(action, 'optimist.id', -1),
      noResults: false,
      prices: [],
    };
  },

  [resolve(FACETING_SEARCH_REQUEST)](
    state: ReduxState,
    action: FacetingSearchRequestResolvedAction,
  ): ReduxState {
    // If there is another request in progress
    if (get(action, 'optimist.id') !== state.lastRequestId) {
      return state;
    }
    const { products } = action.payload.search;
    const productsSelectedForComparison = get(state, 'productsSelectedForComparison', []).filter(
      selectedProductId => products.some(product => product.id === selectedProductId),
    );

    const pertimmInterpretations = get(
      action.payload,
      'interpretation_explanation.interpretations',
      [],
    ).reduce((acc, interpretation) => {
      if (interpretation.filter_name === PERTIMM_DEPARTURE_MIN_DATE) {
        acc.minDate = getTimestamp(interpretation.filter_value, PERTIMM_INTERPRETATION_DATE_FORMAT);
      }
      if (interpretation.filter_name === PERTIMM_DEPARTURE_MAX_DATE) {
        acc.maxDate = getTimestamp(interpretation.filter_value, PERTIMM_INTERPRETATION_DATE_FORMAT);
      }
      return acc;
    }, {});

    return {
      ...state,
      banners: parseBanners(action.payload.banners),
      pertimmInterpretations,
      // If the catalog of facets is empty (e.g. "no results"), keep the previous state
      facets: !isEmpty(action.payload.catalogs)
        ? catalogToState(action.payload.catalogs)
        : state.facets,
      activeFacets: filtersToActiveFacetsState(action.payload.filters),
      isLoading: false,
      filters: filtersToState(action.payload.filters),
      products,
      query: { ...state.query, label: decodeHTML(action.payload.query) },
      redirection: action.payload.redirection,
      productsSelectedForComparison,
      noResults: !action.payload.redirection && products.length === 0,
    };
  },

  [reject(FACETING_SEARCH_REQUEST)](state: ReduxState): ReduxState {
    return {
      ...state,
      isLoading: false,
      noResults: true,
    };
  },

  [FACETING_PRICES_REQUEST](state: ReduxState): ReduxState {
    return {
      ...state,
      isPriceLoading: true,
      prices: [],
    };
  },

  [resolve(FACETING_PRICES_REQUEST)](state: ReduxState, action: *): ReduxState {
    const { resorts } = action.payload;
    return {
      ...state,
      isPriceLoading: false,
      prices: resorts,
      scrollToResults: false,
    };
  },

  [reject(FACETING_PRICES_REQUEST)](state: ReduxState): ReduxState {
    return {
      ...state,
      isPriceLoading: false,
      prices: [],
      scrollToResults: true,
    };
  },

  [FACETING_CHILDCARE_REQUEST](state: ReduxState): ReduxState {
    return {
      ...state,
      isLoadingChildcareAvailabilities: true,
      childcareAvailabilities: [],
    };
  },

  [resolve(FACETING_CHILDCARE_REQUEST)](state: ReduxState, action: *): ReduxState {
    const { childcareSearch } = action.payload;
    return {
      ...state,
      isLoadingChildcareAvailabilities: false,
      childcareAvailabilities: childcareSearch,
    };
  },

  [reject(FACETING_CHILDCARE_REQUEST)](state: ReduxState): ReduxState {
    return {
      ...state,
      isLoadingChildcareAvailabilities: false,
      childcareAvailabilities: [],
    };
  },

  [FACETING_SET_FILTERS](state: ReduxState, action: SetFiltersAction): ReduxState {
    return {
      ...state,
      filters: action.payload.filters,
    };
  },

  [FACETING_SET_INITIAL_PRICES](
    state: ReduxState,
    action: SetInitialProductPricesAction,
  ): ReduxState {
    return {
      ...state,
      prices: action.payload.initialProductPrices,
      isPriceLoading: false,
    };
  },

  [FACETING_SET_IS_SEARCH_ENABLED](state: ReduxState, action): ReduxState {
    return {
      ...state,
      isSearchEnabled: action.payload.isSearchEnabled,
    };
  },

  [FACETING_SET_KIDS_VALIDATION_STATE](
    state: ReduxState,
    action: Action<{ kidsValidationState: Array<ValidationState> }>,
  ): ReduxState {
    return {
      ...state,
      kidsValidationState: action.payload.kidsValidationState,
    };
  },

  [FACETING_SET_PRODUCT_COMPARISON_SELECTION_MODE](state: ReduxState, action): ReduxState {
    return {
      ...state,
      isSelectionModeEnabled: action.payload.enable,
      productsSelectedForComparison: [],
    };
  },

  [FACETING_TOGGLE_PRODUCT_SELECTED_FOR_COMPARISON](state, action) {
    const productsCurrentlySelected = get(state, 'productsSelectedForComparison');
    const productToBeToggled = get(action, 'payload.productId', null);
    if (!productToBeToggled) {
      return state;
    }
    if (includes(productsCurrentlySelected, productToBeToggled)) {
      return {
        ...state,
        productsSelectedForComparison: productsCurrentlySelected.filter(
          p => p !== productToBeToggled,
        ),
      };
    }
    if (productsCurrentlySelected.length < MAX_NUMBER_OF_SELECTED_PRODUCTS) {
      return {
        ...state,
        productsSelectedForComparison: [...productsCurrentlySelected, productToBeToggled],
      };
    }
    return state;
  },

  [SYNC_WITH_LOCATION](state: ReduxState, action: Action<*>): ReduxState {
    const { payload: { location: { queryParams } } = {} } = action;

    if (!queryParams[QUERY_QUERY_PARAM]) {
      return state;
    }

    return {
      ...state,
      query: { ...state.query, label: queryParams[QUERY_QUERY_PARAM] },
    };
  },

  [FACETING_SET_SEARCH_QUERY](
    state: ReduxState,
    {
      payload: { value },
    },
  ): ReduxState {
    if (isUndefined(value)) {
      return state;
    }

    return {
      ...state,
      query: { ...value, label: decodeHTML(value.label) },
    };
  },

  [FACETING_CLEAR_FACETS](state: ReduxState, action): ReduxState {
    return {
      ...state,
      activeFacets: {
        ...state.activeFacets,
        [action.payload.categoryId]: {},
      },
    };
  },

  [FACETING_TOGGLE_FACET_SELECTED](state: ReduxState, action): ReduxState {
    const facet = action.payload;
    const categoryActiveFacets = state.activeFacets[facet.categoryId];
    let newState;

    if (facet.isSelected) {
      // lodash/fp
      // return set(`activeFacets.${facet.category}.${facet.value}`, action.payload.isSelected, state);
      newState = {
        ...categoryActiveFacets,
        [facet.value]: facet.isSelected,
      };
    } else {
      const { [facet.value]: _, ...rest } = categoryActiveFacets; // eslint-disable-line
      newState = rest;
    }

    return {
      ...state,
      activeFacets: {
        ...state.activeFacets,
        [facet.categoryId]: {
          ...newState,
        },
      },
    };
  },

  [FACETING_SET_IS_DATE_REQUIRED](state: ReduxState, action): ReduxState {
    return {
      ...state,
      isDateRequired: action.payload.isDateRequired,
    };
  },

  [FACETING_UPDATE_FILTERS](state: ReduxState, action: SetFiltersAction): ReduxState {
    return {
      ...state,
      filters: action.payload.filters,
      activeFacets: removeUnselectedFiltersFromActiveFacetsState(
        // $FlowFixMe
        action.payload.filters,
        state.activeFacets,
      ),
    };
  },
});
